/**
 * MobileViewport — makes phones behave like the desktop build.
 *
 *  - Kills the browser's own zoom (iOS ignores `user-scalable=no`), so a
 *    stray pinch or double-tap can't leave the page scaled and offset.
 *  - Turns a two-finger pinch into the game's camera zoom instead, via the
 *    stage's `zoomBy` hook.
 *  - The game is landscape-only on touch: in portrait a "rotate" overlay
 *    covers the screen and the game pauses.
 */

import { bus } from '@/core/EventBus';
import { stageManager } from '@/core/StageManager';
import { IS_TOUCH } from './TouchControls';

/** Pinch below/above these factors is ignored as noise. */
const PINCH_DEADZONE = 0.01;

export function installMobileViewport(canvas: HTMLCanvasElement): void {
  if (!IS_TOUCH) return;
  blockBrowserZoom();
  installPinchZoom(canvas);
  installRotateGate();
}

/** Safari gesture events + multi-touch scroll = page zoom. Refuse all of it. */
function blockBrowserZoom(): void {
  const stop = (e: Event): void => e.preventDefault();
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(type, stop, { passive: false });
  }
  document.addEventListener('dblclick', stop, { passive: false });
  document.addEventListener('touchmove', (e) => {
    if ((e as TouchEvent).touches.length > 1) e.preventDefault();
  }, { passive: false });
  // Double-tap-to-zoom shows up as two taps in quick succession.
  let lastTap = 0;
  document.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTap < 320) e.preventDefault();
    lastTap = now;
  }, { passive: false });
}

/** Two fingers on the canvas scale the stage camera. */
function installPinchZoom(canvas: HTMLCanvasElement): void {
  const points = new Map<number, { x: number; y: number }>();
  let lastDist = 0;

  const dist = (): number => {
    const [a, b] = [...points.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (points.size === 2) lastDist = dist();
  });
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'touch' || !points.has(e.pointerId)) return;
    points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (points.size !== 2) return;
    e.preventDefault();
    const d = dist();
    if (lastDist > 0 && d > 0) {
      const factor = d / lastDist;
      if (Math.abs(factor - 1) > PINCH_DEADZONE) {
        stageManager.currentStage()?.zoomBy?.(factor);
      }
    }
    lastDist = d;
  }, { passive: false });
  const drop = (e: PointerEvent): void => {
    points.delete(e.pointerId);
    if (points.size < 2) lastDist = 0;
  };
  canvas.addEventListener('pointerup', drop);
  canvas.addEventListener('pointercancel', drop);
  canvas.addEventListener('pointerleave', drop);
}

/** Portrait = rotate prompt + paused game. Landscape plays as on desktop. */
function installRotateGate(): void {
  const style = document.createElement('style');
  style.textContent = `
#rotate-gate { position: fixed; inset: 0; z-index: 300; display: none; align-items: center; justify-content: center;
  background: #041016; color: #fff5dc; font-family: 'Inter', system-ui, sans-serif; text-align: center; padding: 24px; }
#rotate-gate .rg-inner { max-width: 320px; }
#rotate-gate .rg-phone { width: 76px; height: 126px; margin: 0 auto 22px; border: 3px solid #ffd070; border-radius: 12px;
  animation: rg-turn 2.2s ease-in-out infinite; }
#rotate-gate .rg-title { font-size: 20px; font-weight: 800; letter-spacing: 0.14em; color: #ffd070; }
#rotate-gate .rg-text { font-size: 13px; opacity: 0.75; margin-top: 8px; line-height: 1.5; }
@keyframes rg-turn { 0%, 30% { transform: rotate(0); } 60%, 100% { transform: rotate(-90deg); } }
@media (orientation: portrait) { body.is-touch #rotate-gate { display: flex; } }
`;
  document.head.appendChild(style);
  const gate = document.createElement('div');
  gate.id = 'rotate-gate';
  gate.innerHTML = `
    <div class="rg-inner">
      <div class="rg-phone"></div>
      <div class="rg-title">ROTATE YOUR DEVICE</div>
      <div class="rg-text">Evolve is played in landscape. Turn your phone sideways to keep swimming.</div>
    </div>`;
  document.body.appendChild(gate);
  // The gate only shows on touch devices; mark the body from the start so
  // it works before any stage builds its touch pad.
  document.body.classList.add('is-touch');

  let pausedByGate = false;
  const sync = (): void => {
    const portrait = window.matchMedia('(orientation: portrait)').matches;
    if (portrait && !pausedByGate) {
      pausedByGate = true;
      bus.emit('game:paused', undefined);
    } else if (!portrait && pausedByGate) {
      pausedByGate = false;
      bus.emit('game:resumed', undefined);
    }
    // Best effort: browsers only honour this in fullscreen, and throw otherwise.
    if (!portrait) {
      const orientation = screen.orientation as (ScreenOrientation & { lock?: (o: string) => Promise<void> }) | undefined;
      orientation?.lock?.('landscape').catch(() => { /* not allowed here */ });
    }
  };
  window.addEventListener('orientationchange', () => setTimeout(sync, 80));
  window.matchMedia('(orientation: portrait)').addEventListener?.('change', sync);
  sync();
}
